// Lista de Promos venta: estado de la fila, filtros y orden. Todo corre en el cliente sobre lo cargado.
import { LIFECYCLE_STATUS_LABEL, matchesNameSearch } from '@/shared/patterns/Lifecycle';
import type { SalePromotionListItem, SalePromotionScope } from '../api/promotionsContract';
import { displayStatusOf, type PromotionDisplayStatus } from './promotionStatus';
import { PRODUCT_CATEGORY_OPTIONS, PRODUCT_TIER_OPTIONS } from './salePromotionFormOptions';
import { isSystemPromotion } from './systemPromotion';

/** El estado de la fila: una Activa sin fin se lee Permanente, como en el mock. */
export type PromotionListStatus = PromotionDisplayStatus | 'PERMANENT';

/** Cada estado de la lista ya es un estado visual común: se nombra con el mismo diccionario. */
export const LIST_STATUS_LABEL: Record<PromotionListStatus, string> = LIFECYCLE_STATUS_LABEL;

/** Orden operativo de la columna Estado. */
const STATUS_RANK: Record<PromotionListStatus, number> = {
    ACTIVE: 0,
    PERMANENT: 1,
    SCHEDULED: 2,
    PAUSED: 3,
    DRAFT: 4,
    EXPIRED: 5,
    ARCHIVED: 6,
};

export function listStatusOf(promo: SalePromotionListItem, now: Date): PromotionListStatus {
    const status = displayStatusOf(promo, now);
    return status === 'ACTIVE' && promo.validUntil === null ? 'PERMANENT' : status;
}

export type SegmentationFilter = 'ALL' | 'OPEN' | 'SEGMENTS' | 'SK_CREDIT';

export const SEGMENTATION_FILTER_OPTIONS: { value: SegmentationFilter; label: string }[] = [
    { value: 'ALL', label: 'Toda segmentación' },
    { value: 'OPEN', label: 'Abierta a todos' },
    { value: 'SK_CREDIT', label: 'Con crédito SK activo' },
    { value: 'SEGMENTS', label: 'Por segmento' },
];

/** `ARCHIVED` no filtra aquí: cambia la lectura del back a solo archivadas. */
export type StatusFilter = 'ALL' | PromotionListStatus;

/** Los estados del filtro, en el orden operativo de la columna. */
export const LIST_FILTER_STATUSES = [
    'ACTIVE',
    'PERMANENT',
    'SCHEDULED',
    'PAUSED',
    'DRAFT',
    'EXPIRED',
    'ARCHIVED',
] as const satisfies readonly PromotionListStatus[];

export interface PromotionListFilters {
    /** Parte del nombre; vacío no filtra. */
    search: string;
    segmentation: SegmentationFilter;
    status: StatusFilter;
}

export const EMPTY_LIST_FILTERS: PromotionListFilters = {
    search: '',
    segmentation: 'ALL',
    status: 'ALL',
};

function hasSegments(promo: SalePromotionListItem): boolean {
    return (promo.segmentation.segmentIds?.length ?? 0) > 0;
}

function matchesSegmentation(promo: SalePromotionListItem, filter: SegmentationFilter): boolean {
    const skCredit = promo.segmentation.requiresActiveSurekeepCredit;
    switch (filter) {
        case 'ALL':
            return true;
        case 'OPEN':
            return !hasSegments(promo) && !skCredit;
        case 'SEGMENTS':
            return hasSegments(promo);
        case 'SK_CREDIT':
            return skCredit;
    }
}

export function filterPromotions(
    items: readonly SalePromotionListItem[],
    filters: PromotionListFilters,
    now: Date,
): SalePromotionListItem[] {
    return items.filter((promo) => {
        if (!matchesNameSearch(promo.name, filters.search)) return false;
        if (!matchesSegmentation(promo, filters.segmentation)) return false;
        if (filters.status === 'ALL' || filters.status === 'ARCHIVED') return true;
        return listStatusOf(promo, now) === filters.status;
    });
}

/** «Abierta a todos», los segmentos por nombre y, si aplica, el crédito SK. */
export function describeSegmentation(
    promo: Pick<SalePromotionListItem, 'segmentation'>,
    segmentNames: ReadonlyMap<string, string>,
): string {
    const ids = promo.segmentation.segmentIds ?? [];
    const skCredit = promo.segmentation.requiresActiveSurekeepCredit;
    const names = ids.map((id) => segmentNames.get(id) ?? id).sort((a, b) => a.localeCompare(b));
    if (names.length === 0) return skCredit ? 'Con crédito SK activo' : 'Abierta a todos';
    return skCredit ? `${names.join(', ')} · con crédito SK` : names.join(', ');
}

const CATEGORY_LABEL = new Map(PRODUCT_CATEGORY_OPTIONS.map((o) => [o.value, o.label]));
const TIER_LABEL = new Map(PRODUCT_TIER_OPTIONS.map((o) => [o.value, o.label]));

const plural = (count: number, one: string, many: string) =>
    `${String(count)} ${count === 1 ? one : many}`;

/** El alcance en una línea; sin nada elegido aplica a todo el catálogo. */
export function describeScope(scope: SalePromotionScope): string {
    const parts = [
        (scope.productCategories ?? []).map((c) => CATEGORY_LABEL.get(c) ?? c).join(', '),
        (scope.tiers ?? []).map((t) => TIER_LABEL.get(t) ?? t).join(', '),
        scope.skus?.length ? plural(scope.skus.length, 'SKU', 'SKUs') : '',
        scope.storeIds?.length ? plural(scope.storeIds.length, 'tienda', 'tiendas') : '',
    ].filter((part) => part !== '');
    return parts.length === 0 ? 'Todo el catálogo' : parts.join(' · ');
}

export type PromotionSortKey = 'name' | 'segmentation' | 'status' | 'redemptions' | 'consumed';

export interface PromotionListSort {
    key: PromotionSortKey;
    direction: 'asc' | 'desc';
}

/** Alfabética por nombre, la convención de las listas del portal. */
export const DEFAULT_LIST_SORT: PromotionListSort = { key: 'name', direction: 'asc' };

export function sortPromotions(
    items: readonly SalePromotionListItem[],
    sort: PromotionListSort,
    now: Date,
    segmentNames: ReadonlyMap<string, string>,
): SalePromotionListItem[] {
    const compare = (a: SalePromotionListItem, b: SalePromotionListItem): number => {
        switch (sort.key) {
            case 'name':
                return a.name.localeCompare(b.name, 'es-MX');
            case 'segmentation':
                return describeSegmentation(a, segmentNames).localeCompare(
                    describeSegmentation(b, segmentNames),
                    'es-MX',
                );
            case 'status':
                return STATUS_RANK[listStatusOf(a, now)] - STATUS_RANK[listStatusOf(b, now)];
            case 'redemptions':
                return a.redemptionsLast30Days - b.redemptionsLast30Days;
            case 'consumed':
                return a.budgetConsumedCents - b.budgetConsumedCents;
        }
    };
    const sign = sort.direction === 'asc' ? 1 : -1;
    const systemFirst = (a: SalePromotionListItem, b: SalePromotionListItem): number =>
        Number(isSystemPromotion(b)) - Number(isSystemPromotion(a));
    // La de sistema va hasta arriba; el nombre desempata para que las filas no brinquen.
    return [...items].sort(
        (a, b) =>
            systemFirst(a, b) || sign * compare(a, b) || a.name.localeCompare(b.name, 'es-MX'),
    );
}
